import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Switch,
    TextField,
    Theme,
    Typography,
    useMediaQuery
} from '@mui/material';
import authService, { ExtendedLeadPayloadData } from '../../services/authService';
import { useFormik } from 'formik';
import AppointmentAddressBox from '../../ui-component/AppointmentAddressBox';
import StickyFormFooter from '../../ui-component/form/sticky-form-footer';
import UpdatedStyleWrapper from '../../ui-component/updated-style-wrapper';
import { useCallback, useMemo, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import InitAPI from '../../services/InitService';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SnackBarTypes } from '../../store/snackbarReducer';
import useShowSnackbar from '../../hooks/useShowSnackbar';
import * as Yup from 'yup';
import { useAppDispatch } from '../../hooks/redux';
import { startSubmitting, stopSubmitting } from '../../store/slices/SubmittingSlice';

import { IndustryOptions } from 'ui-component/google-places-autocomplete/types';
import GooglePlacesAutocomplete from 'ui-component/google-places-autocomplete/GooglePlacesAutocomplete';
import InputRow from 'ui-component/form/InputRow';

const initialValues: ExtendedLeadPayloadData = {
    email: '',
    firstname: '',
    lastname: '',
    address: {
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country: ''
    },
    company: {
        industry: '',
        name: '',
        subscription_type: ''
    },
    payment_processor_price_id: '',
    with_trial: false,
    trial_days: ''
};

const labels: Record<string, string> = {
    firstname: 'First Name',
    lastname: 'Last Name',
    email: 'Email'
};

const userFields = ['firstname', 'lastname', 'email'] as const;

const CreateLead = () => {
    const dispatch = useAppDispatch();
    const [url, setUrl] = useState<string | null>(null);
    const { user } = useAuth();
    const { showSnackbar } = useShowSnackbar();
    const companyId = user?.currentCompany.id;
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('mobile'));

    const [generateLead] = authService.useSaveExtendedLeadMutation();
    const { data: initData } = InitAPI.useInitQuery({ params: { for_backoffice: true } });

    const prices = useMemo(() => {
        if (!initData) return [];

        return initData.paddle.products.flatMap((product) =>
            product.prices.map(({ id, trial_period, unit_price }) => ({
                label:
                    `${product.name} / ${unit_price.amount / 100} ${unit_price.currency_code}` +
                    (trial_period ? ` / ${trial_period.frequency} ${trial_period.interval} trial` : ''),
                value: id
            }))
        );
    }, [initData]);

    const handleCopyUrl = useCallback(() => {
        if (url) {
            navigator.clipboard.writeText(url).then(() => {
                showSnackbar({ alertSeverity: SnackBarTypes.Success, message: 'Link copied!' });
            });
        }
    }, [showSnackbar, url]);

    const onSubmit = useCallback(
        (data: ExtendedLeadPayloadData) => {
            if (companyId) {
                dispatch(startSubmitting());
                generateLead({ companyId, data })
                    .unwrap()
                    .then((res) => {
                        setUrl(res.url);
                    })
                    .catch((err) => {
                        showSnackbar({
                            alertSeverity: SnackBarTypes.Error,
                            message: err.message ?? JSON.stringify(err)
                        });
                    })
                    .finally(() => {
                        dispatch(stopSubmitting());
                    });
            }
        },
        [companyId, dispatch, generateLead, showSnackbar]
    );

    const form = useFormik<ExtendedLeadPayloadData>({
        enableReinitialize: true,
        onSubmit,
        initialValues,
        validationSchema: Yup.object().shape({
            email: Yup.string().trim().required().email().label(labels.email),
            firstname: Yup.string().trim().required().label(labels.firstname),
            lastname: Yup.string().trim().required().label(labels.lastname),
            address: Yup.object().shape({
                address: Yup.string().trim().required().label('Address')
            }),
            company: Yup.object().shape({
                industry: Yup.string().trim().required().label('Industry'),
                name: Yup.string().trim().required().label('Business Name'),
                subscription_type: Yup.string().trim().required().label('Subscription Type'),
                site: Yup.string().trim().nullable(),
                phone: Yup.string().trim().nullable(),
                opening_hours: Yup.object().nullable(),
                google_place_id: Yup.string().trim().nullable()
            }),
            payment_processor_price_id: Yup.string().trim().required().label('Subscription Price Id'),
            with_trial: Yup.boolean().required().label('With Trial'),
            trial_days: Yup.mixed().when('with_trial', {
                is: true,
                then: Yup.number().integer().min(0).required().label('Trial Days'),
                otherwise: Yup.number().nullable().notRequired()
            })
        })
    });

    const { values, touched, handleChange, handleBlur, handleSubmit, errors, setFieldValue, resetForm } = form;

    const onGenerateNewLabel = useCallback(() => {
        setUrl(null);
        resetForm();
    }, [resetForm]);

    const clearPlaceFields = () => {
        setFieldValue('address', initialValues.address);
        setFieldValue('company.industry', '');
        setFieldValue('company.phone', '');
        setFieldValue('company.opening_hours', '');
        setFieldValue('company.google_place_id', '');
        setFieldValue('company.site', '');
    };

    return (
        <UpdatedStyleWrapper>
            <Box
                sx={{
                    display: 'grid',
                    minHeight: '100%',
                    gridTemplateRows: '100%',
                    gridTemplateColumns: '100%',
                    pb: isMobile ? 0 : '20px',

                    '& > .MuiPaper-root:not(.MuiAlert-root)': {
                        px: isMobile ? 2 : 3,
                        pt: isMobile ? 0 : 3,
                        pb: isMobile ? 0 : 1,
                        borderRadius: isMobile ? 0 : '12px',
                        width: '1320px',
                        maxWidth: '100%',
                        margin: '0 auto'
                    }
                }}
            >
                <Paper elevation={0}>
                    <Typography variant="h3" mb={2}>
                        Lead URL Generator
                    </Typography>

                    {url ? (
                        <Stack spacing={2}>
                            <Alert
                                severity="info"
                                action={
                                    <IconButton color="inherit" onClick={handleCopyUrl}>
                                        <ContentCopyIcon />
                                    </IconButton>
                                }
                            >
                                <AlertTitle>URL for client: </AlertTitle>
                                {url}
                            </Alert>
                            <Box>
                                <Button onClick={onGenerateNewLabel} variant="contained" color="error">
                                    Generate Another Lead
                                </Button>
                            </Box>
                        </Stack>
                    ) : (
                        <Grid container spacing={2} component="form" noValidate autoComplete="off" onSubmit={handleSubmit}>
                            {userFields.map((item) => (
                                <Grid item xs={12} sm={item !== 'email' ? 6 : undefined} key={item}>
                                    <TextField
                                        fullWidth
                                        id={item}
                                        name={item}
                                        label={labels[item]}
                                        value={values[item]}
                                        error={!!errors[item] && !!touched[item]}
                                        helperText={touched[item] ? errors[item] : undefined}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </Grid>
                            ))}

                            <Grid item xs={12}>
                                <GooglePlacesAutocomplete
                                    id="company.name"
                                    name="company.name"
                                    label="Business Name"
                                    error={!!errors.company?.name && !!touched.company?.name}
                                    helperText={touched.company?.name ? errors.company?.name : undefined}
                                    inputValue={values.company.name}
                                    onInputChange={(newValue) => {
                                        // Reset some fields
                                        clearPlaceFields();

                                        setFieldValue('company.name', newValue);
                                    }}
                                    onPlaceRetrieved={(place) => {
                                        // Reset some fields
                                        clearPlaceFields();

                                        setFieldValue('address', place.address);
                                        setFieldValue('company.industry', place.industry);
                                        setFieldValue('company.phone', place.phone);
                                        setFieldValue('company.opening_hours', place.opening_hours);
                                        setFieldValue('company.google_place_id', place.google_place_id);
                                        setFieldValue('company.site', place.site);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="company.industry"
                                    name="company.industry"
                                    label="Industry"
                                    select
                                    value={values.company.industry}
                                    onBlur={handleBlur}
                                    error={!!errors.company?.industry && !!touched.company?.industry}
                                    helperText={touched.company?.industry ? errors.company?.industry : undefined}
                                    onChange={(e) => setFieldValue('company.industry', e.target.value)}
                                >
                                    {Object.entries(IndustryOptions).map(([value, label]) => (
                                        <MenuItem value={value} key={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <AppointmentAddressBox
                                    fullWidth
                                    id="address"
                                    name="address"
                                    label="Address"
                                    value={values.address}
                                    onBlur={handleBlur}
                                    onChange={(v) => setFieldValue('address', v)}
                                    error={!!touched.address && !!errors.address?.address}
                                    helperText={touched.address ? errors.address?.address : undefined}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="payment_processor_price_id"
                                    name="payment_processor_price_id"
                                    label="Subscription Type"
                                    select
                                    value={values.payment_processor_price_id}
                                    onBlur={handleBlur}
                                    error={!!errors.payment_processor_price_id && !!touched.payment_processor_price_id}
                                    helperText={touched.payment_processor_price_id ? errors.payment_processor_price_id : undefined}
                                    onChange={(e) => {
                                        setFieldValue('payment_processor_price_id', e.target.value);

                                        // find subscription type by selected price id
                                        const selectedPaddleProduct = initData?.paddle.products.find((pro) =>
                                            pro.prices.some((pri) => pri.id === e.target.value)
                                        );
                                        if (selectedPaddleProduct)
                                            setFieldValue('company.subscription_type', selectedPaddleProduct.custom_data.limitName);
                                    }}
                                >
                                    {prices.map((price) => (
                                        <MenuItem value={price.value} key={price.value}>
                                            {price.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <InputRow label="Trial Registration">
                                    <Switch
                                        aria-label="Trial Registration"
                                        checked={values.with_trial}
                                        name="with_trial"
                                        value={values.with_trial}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            setFieldValue('with_trial', e.target.checked);

                                            if (e.target.checked) {
                                                setFieldValue('trial_days', 14);
                                            } else {
                                                setFieldValue('trial_days', '');
                                            }
                                        }}
                                    />
                                </InputRow>
                            </Grid>

                            {values.with_trial && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="trial_days"
                                        name="trial_days"
                                        label="Trial Days"
                                        type="number"
                                        value={values.trial_days}
                                        onBlur={handleBlur}
                                        error={!!errors.trial_days && !!touched.trial_days}
                                        helperText={touched.trial_days ? errors.trial_days : undefined}
                                        onChange={handleChange}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <StickyFormFooter>
                                    <Button color="primary" variant="text" className="forcedBg" onClick={() => resetForm()}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" color="primary" variant="contained">
                                        Generate Lead
                                    </Button>
                                </StickyFormFooter>
                            </Grid>
                        </Grid>
                    )}
                </Paper>
            </Box>
        </UpdatedStyleWrapper>
    );
};

export default CreateLead;
